import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ============================================
// HOME HEADER — Single CTA + Type Picker
// ============================================

export interface HomeHeaderProps {
  totalCasesCount: number;
  filteredCasesCount: number;
  nonEmptyCategoriesCount: number;
  isFiltering: boolean;
}

export function HomeHeader({
  totalCasesCount,
  filteredCasesCount,
  nonEmptyCategoriesCount,
  isFiltering,
}: HomeHeaderProps) {
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    }
    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showPicker]);

  const getSubtitle = () => {
    if (totalCasesCount === 0) {
      return "Prepare for peer-to-peer reviews";
    }

    if (isFiltering) {
      if (filteredCasesCount === 0) {
        return "No matching cases";
      }
      return `Showing ${filteredCasesCount} of ${totalCasesCount} case${
        totalCasesCount === 1 ? "" : "s"
      }`;
    }

    return `${totalCasesCount} case${
      totalCasesCount === 1 ? "" : "s"
    } across ${nonEmptyCategoriesCount} categor${
      nonEmptyCategoriesCount === 1 ? "y" : "ies"
    }`;
  };

  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Your Cases</h2>
          <p className="text-sm text-slate-500 mt-0.5">{getSubtitle()}</p>
        </div>
        <div className="relative" ref={pickerRef}>
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-xl shadow-md shadow-teal-500/20 hover:from-teal-600 hover:to-teal-700 hover:shadow-lg hover:shadow-teal-500/30 transition-all"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            New Case
            <svg
              className={`w-3.5 h-3.5 transition-transform ${
                showPicker ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </button>

          {/* Type picker dropdown */}
          {showPicker && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden z-20">
              <div className="p-1.5">
                {/* PA Letter option */}
                <button
                  onClick={() => {
                    setShowPicker(false);
                    navigate("/new?mode=prior-auth");
                  }}
                  className="w-full text-left p-3 rounded-lg hover:bg-teal-50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-teal-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <svg
                        className="w-5 h-5 text-slate-400 group-hover:text-teal-600 transition-colors"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-teal-700 transition-colors">
                        Prior Auth Letter
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        AI-generated medical necessity letter for PA submissions
                      </p>
                    </div>
                  </div>
                </button>

                {/* Divider */}
                <div className="mx-3 my-0.5 border-t border-slate-100" />

                {/* P2P Prep option */}
                <button
                  onClick={() => {
                    setShowPicker(false);
                    navigate("/new");
                  }}
                  className="w-full text-left p-3 rounded-lg hover:bg-slate-50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center flex-shrink-0 transition-colors">
                      <svg
                        className="w-5 h-5 text-slate-400 group-hover:text-slate-700 transition-colors"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-slate-900 transition-colors">
                        P2P Call Prep
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Talking points, rebuttals, and call scripts for
                        peer-to-peer review
                      </p>
                      <span className="inline-flex items-center mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
                        PRO
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
